import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import PropTypes from 'prop-types';
import { withRouter } from "react-router-dom";
import { Row, Col, Form, Input, Button, Select, Checkbox, Spin } from 'antd';
import IntlMessages from "../../../components/utility/intlMessages";
import { DvAxiosRequest, DvAxiosRequestEx, DvIntlMessages } from "../../../components/utility/dvGeneralUtilities";
import systemActions from '../../../redux/systemInfos/actions';
import styled from "styled-components";
import axios from "axios";


const Container = styled.div`
.ant-form-item label {
  width: 180px;
}
.full-width .ant-checkbox-wrapper {
  width: 100%;
}
.ant-checkbox-wrapper {
  width: 100%;
}
`;

const { updateUser, getAvailablePermissions } = systemActions;

function EditUserForm({
  userId,
  availablePermissions,
  match,
  getAvailablePermissions,
  updateUser,
  closeModal
}) {
  const [form] = Form.useForm();

  const [isLoading, setIsLoading] = useState(false);
  const [userDetails, setUserDetails] = useState();
  const [permissions, setPermissions] = useState([]);

  useEffect(() => {
    if (!availablePermissions.isLoaded) getAvailablePermissions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setIsLoading(true);

    const prom1 = DvAxiosRequestEx({
        url: `/ehrAdmin/users/${userId}`,
        method: 'get',
        data: {},
    })
      .then((resp) => {
        const userDetails = resp.data;
        setUserDetails(userDetails);
        form.setFieldValue('firstName', userDetails.firstName);
        form.setFieldValue('lastName', userDetails.lastName);
        form.setFieldValue('email', userDetails.email);
        form.setFieldValue('phone', userDetails.phone);
        form.setFieldValue('specialty', userDetails.specialty);
        form.setFieldValue('role', userDetails.roles[0]?.name);
      });

    const prom2 = DvAxiosRequestEx({
        url: `/ehrAdmin/users/${userId}/permissions`,
        method: 'get',
        data: {},
    })
      .then((resp) => {
        setPermissions(resp.data);
      });

    Promise.all([prom1, prom2])
      .then(() => setIsLoading(false));
  }, [userId]);


  const onFinish = (values) => {
    const bodyToSend = {
       ...values,
       clinicId: userDetails?.clinicId,
       permissions: permissions ?? []
    };
    delete bodyToSend.confirm;
    updateUser(bodyToSend, userDetails?.clinicId, userDetails?.id);
    closeModal();
  };

  const emailValidator = (rule, value) => {
    const emailRegEx = /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(.\w{2,3})+$/;
    if (rule['required'] && !value) {
      return Promise.reject(DvIntlMessages('registration.requiredEmail'));
    }
    if (value && value.length !== 0 && !emailRegEx.test(String(value).toLowerCase())) {
      return Promise.reject(DvIntlMessages('registration.invalidEmailType'));
    }
    return Promise.resolve();
  };

  const phoneNumberValidator = (rule, value, callback) => {
    const phoneNumberRegEx = /^\+?([ ]?[-.(]?[\d]+[-.)]?)+$/;
    if (value && value.length !== 0 && !phoneNumberRegEx.test(String(value))) {
      callback(<IntlMessages id="error.phoneNumberValidationError" />);
    } else {
      callback();
    }
  };

  return (
    <Container>
      <Spin spinning={isLoading}>
        <Row style={{ marginTop: "25px" }}>
          <Col span={24}>
            <Form form={form} name="createNewClinic" onFinish={onFinish}>
              <Form.Item
                name="firstName"
                label={<IntlMessages id="general.firstName" />}
                rules={[{ required: true }]}
              >
                <Input placeholder={DvIntlMessages("general.firstName")} autoComplete="firstname" />
              </Form.Item>

              <Form.Item
                name="lastName"
                label={<IntlMessages id="general.lastName" />}
                rules={[{ required: true }]}
              >
                <Input placeholder={DvIntlMessages("general.lastName")} autoComplete="lastname" />
              </Form.Item>

              <Form.Item
                name="email"
                label={<IntlMessages id="general.email" />}
                rules={[
                  {
                    required: true,
                    validator: emailValidator
                  },
                ]}
              >
                <Input placeholder={DvIntlMessages("general.email")} />
              </Form.Item>

              <Form.Item
                name="phone"
                label={<IntlMessages id="general.phone" />}
                rules={[
                  {
                    required: false,
                    validator: phoneNumberValidator
                  },
                  {
                    message: DvIntlMessages("input.noLongerThan20"),
                    max: 20
                  }
                ]}
              >
                <Input placeholder={DvIntlMessages("general.phone")} />
              </Form.Item>

              <Form.Item
                name="specialty"
                label={<IntlMessages id="general.specialty" />}
              >
                <Input placeholder={DvIntlMessages("general.specialty")} />
              </Form.Item>

              <Form.Item
                name="role"
                label={<IntlMessages id="general.role" />}
                rules={[{
                  required: true,
                  message: DvIntlMessages('createUserForm.role.required')
                }]}
              >
                <Select>
                  <Select.Option value="doctor">
                    <IntlMessages id="general.doctor" />
                  </Select.Option>
                </Select>
              </Form.Item>

              <Form.Item
                label="Permissions">
                <Spin spinning={availablePermissions.isLoading}>
                  <Checkbox.Group
                    value={permissions}
                    className="full-width"
                    onChange={(checkedValues => setPermissions(checkedValues))}
                    options={availablePermissions.items.map(p => {
                      return { label: p, value: p };
                    })} />
                </Spin>
              </Form.Item>

              <Row justify='end' gutter={[16, 16]}>
                <Col>
                  <Button onClick={closeModal}>
                    <IntlMessages id="general.cancel" />
                  </Button>
                </Col>
                <Col>
                  <Button type="primary" htmlType="submit">
                    <IntlMessages id="general.submit" />
                  </Button>
                </Col>
              </Row>

            </Form>
          </Col>
        </Row>
      </Spin>
    </Container>
  )
}

EditUserForm.propTypes = {
  userId: PropTypes.number.isRequired,
  closeModal: PropTypes.func.isRequired,
  getAvailablePermissions: PropTypes.func.isRequired,
  updateUser: PropTypes.func.isRequired
};

const mapActionsToProps = {
  updateUser,
  getAvailablePermissions,
};

function mapStateToProps(state) {
  return {
    availablePermissions: state.SystemInfos.get('availablePermission'),
  };
}

const wrappedEditUserForm = withRouter(EditUserForm);
const connectedEditUserForm = connect(mapStateToProps, mapActionsToProps)(wrappedEditUserForm);
export default connectedEditUserForm;
